import api from './api';

const cancelUploadRequests = new Map();

function normalizeFilesListParams(params = {}) {
    const search = String(
        params.search ?? params.title ?? params.query ?? ''
    ).trim();
    const sortOrder = params.sortOrder || params.sortDirection;
    const mapped = {};

    if (params.folderId) mapped.folderId = params.folderId;
    if (params.page) mapped.page = params.page;
    if (params.limit) mapped.limit = params.limit;
    if (params.pageSize) mapped.pageSize = params.pageSize;
    if (params.sortBy) mapped.sortBy = params.sortBy;
    if (sortOrder) mapped.sortOrder = sortOrder;
    if (search) mapped.search = search;

    if (params.filterType) {
        mapped.type = params.filterType;
    }
    if (params.type && !mapped.type) mapped.type = params.type;
    if (params.mediaType) mapped.mediaType = params.mediaType;

    if (params.status) {
        mapped.status = params.status === 'failed' ? 'error' : params.status;
    }
    if (params.hasDRM === true || params.hasDRM === 'true') {
        mapped.hasDRM = 'true';
    } else if (params.hasDRM === false || params.hasDRM === 'false') {
        mapped.hasDRM = 'false';
    }

    return mapped;
}

function isThumbnailAsset(asset) {
    const type = String(asset?.type || '').toUpperCase();
    return type === 'THUMBNAIL';
}

function collectFileAssets(file) {
    if (!file) return [];
    const merged = [
        ...(Array.isArray(file.fileAssets) ? file.fileAssets : []),
        ...(Array.isArray(file.assets) ? file.assets : []),
        ...(Array.isArray(file.media?.assets) ? file.media.assets : [])
    ];
    const seen = new Set();
    return merged.filter((asset) => {
        const key = asset?.id || asset?.path || asset?.url;
        if (!key) return true;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

/**
 * Asset THUMBNAIL preferido: isPrimary, depois frame/uploaded, depois maior version.
 */
export function resolveFileThumbnailAsset(file) {
    const thumbs = collectFileAssets(file).filter(isThumbnailAsset);
    if (!thumbs.length) return null;

    const primary = thumbs.find((asset) => asset?.metadata?.isPrimary === true);
    if (primary) return primary;

    const custom = thumbs.find((asset) => {
        const source = asset?.metadata?.source;
        return source === 'frame' || source === 'uploaded';
    });
    if (custom) return custom;

    return [...thumbs].sort((a, b) => {
        const versionA = Number(a?.metadata?.version) || 0;
        const versionB = Number(b?.metadata?.version) || 0;
        return versionB - versionA;
    })[0];
}

function withThumbnailVersion(url, metadata) {
    if (!url || typeof url !== 'string') return '';
    const version = metadata?.version;
    if (version === undefined || version === null || version === '') return url;
    if (/[?&](v|version)=/.test(url)) return url;
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}v=${encodeURIComponent(String(version))}`;
}

/**
 * URL da capa na listagem/detalhe (crop frame, upload ou default).
 */
/**
 * ID usado nas rotas /files/{fileId} (listagem, detalhe, delete, move).
 */
export function resolveFileApiId(fileOrRouteId) {
    if (!fileOrRouteId) return null;
    if (typeof fileOrRouteId === 'string') return fileOrRouteId;
    return fileOrRouteId.id || fileOrRouteId.media?.id || null;
}

export function resolveFileThumbnailUrl(file) {
    if (!file) return '';

    const direct = file.thumbnailUrl || file.media?.thumbnailUrl;
    if (direct) {
        const asset = resolveFileThumbnailAsset(file);
        return withThumbnailVersion(direct, asset?.metadata);
    }

    const asset = resolveFileThumbnailAsset(file);
    const path = asset?.path || asset?.url || '';
    return withThumbnailVersion(path, asset?.metadata);
}

function appendFilesListParams(queryParams, params) {
    const mapped = normalizeFilesListParams(params);
    Object.entries(mapped).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            queryParams.append(key, String(value));
        }
    });
}

export default {
    async getFiles(params, token) {
        try {
            const headers = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            };

            const queryParams = new URLSearchParams();
            appendFilesListParams(queryParams, params);

            const response = await api.get(`/files?${queryParams.toString()}`, {
                headers
            });
            return response;
        } catch (error) {
            console.error('Erro ao fazer listagem de arquivos:', error);
            throw error;
        }
    },
    async getFilesByFolder(folderId, params, token) {
        try {
            const headers = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            };
            const requestParams = normalizeFilesListParams(params);
            const response = await api.get(`/files?folderId=${folderId}`, {
                headers,
                params: requestParams
            });
            return response;
        } catch (error) {
            console.error('Erro ao fazer listagem de arquivos:', error);
            throw error;
        }
    },
    async getFileById(id, token) {
        try {
            const headers = {
                'Content-Type': 'application/json', 
                'Authorization': `Bearer ${token}`
            };
            const response = await api.get(`/files/${id}`, {
                headers,
            });
            return response;
        } catch (error) {
            console.error('Erro ao fazer listagem do arquivo:', error);
                throw error;
        }
    },
    async deleteFile(id, token) {
        try {
            const fileId = resolveFileApiId(id);
            if (!fileId) {
                throw new Error('ID do arquivo ausente');
            }
            const headers = {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
            };
            const response = await api.delete(`/files/${fileId}`, {
                headers
            });
            return response;
        } catch (error) {
            console.error('Erro ao deletar o arquivo:', error);
            throw error;
        }
    },
    /**
     * POST /files/move — body: { fileIds: string[], targetFolderId: string }
     * @see https://api.videotecaead.com.br/api-docs/
     */
    async moveFiles(token, { fileIds, targetFolderId }) {
        try {
            const ids = Array.isArray(fileIds) ? fileIds.filter(Boolean) : [];
            if (!ids.length || !targetFolderId) {
                throw new Error('fileIds e targetFolderId são obrigatórios');
            }
            const headers = {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
            };
            const response = await api.post(
                '/files/move',
                { fileIds: ids, targetFolderId },
                { headers }
            );
            return response;
        } catch (error) {
            console.error('Erro ao mover arquivos:', error);
            throw error;
        }
    },
    async moveFileToFolder(token, fileId, targetFolderId) {
        const id = resolveFileApiId(fileId);
        return this.moveFiles(token, {
            fileIds: id ? [id] : [],
            targetFolderId
        });
    },
    async editFile(id, payload, token) {
        try {
            const headers = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            };
            const response = await api.put(`/files/${id}`, payload, {
                headers,
            });
            return response;
        } catch (error) {
            console.error('Erro ao editar o arquivo:', error);
                throw error;
        }
    },
    async cancelUpload(id, token) {
        const fileId = resolveFileApiId(id);
        if (!fileId) {
            throw new Error('ID do arquivo ausente para cancelamento');
        }

        const requestKey = String(fileId);
        const existingRequest = cancelUploadRequests.get(requestKey);
        if (existingRequest) return existingRequest;

        const request = api.post(`/files/${encodeURIComponent(fileId)}/cancel`, {}, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        }).catch((error) => {
            const status = error.response?.status;
            if ([400, 404, 409].includes(status)) {
                return error.response;
            }

            console.error('Erro ao cancelar o upload do arquivo:', error);
            throw error;
        }).finally(() => {
            cancelUploadRequests.delete(requestKey);
        });

        cancelUploadRequests.set(requestKey, request);
        return request;
    },
    async reportUploadProgress(id, payload) {
        const fileId = resolveFileApiId(id);
        if (!fileId) return null;
        // A autenticação (Bearer) é injetada pelo interceptor do api
        return api.post(`/files/${encodeURIComponent(fileId)}/upload/progress`, payload);
    },
    async reportUploadClientLog(id, payload) {
        const fileId = resolveFileApiId(id);
        if (!fileId) return null;
        // A autenticação (Bearer) é injetada pelo interceptor do api
        return api.post(`/files/${encodeURIComponent(fileId)}/upload/client-log`, payload);
    },
    cancelUploadOnUnload(id, token) {
        const fileId = resolveFileApiId(id);
        if (!fileId || !token || typeof fetch !== 'function') return false;

        const baseUrl = (api.defaults.baseURL || window.location.origin).replace(/\/+$/, '');
        const endpoint = `${baseUrl}/files/${encodeURIComponent(fileId)}/cancel`;

        try {
            fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: '{}',
                keepalive: true
            }).catch(() => {});
            return true;
        } catch (error) {
            console.error('Erro ao enviar cancelamento durante saída da página:', error);
            return false;
        }
    },
    async cutVideo(id, payload, token) {
        try {
            const headers = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            };
            const response = await api.post(`/files/${id}/video-cut`, payload, {
                headers,
            });
            return response;
        } catch (error) {
            console.error('Erro ao cortar o vídeo:', error);
                throw error;    
        }
    },
    async getStatusCutVideo(id, token) {
        try {
            const headers = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            };
            const response = await api.get(`/files/${id}/video-cut`, {
                headers,
            });
            return response;
        } catch (error) {
            console.error('Erro ao buscar o status do corte do vídeo:', error);
                throw error;    
        }
    },
    async getFileStats(id, token) {
        try {
            const headers = {
                'Content-Type': 'application/json', 
                'Authorization': `Bearer ${token}`
            };
            const response = await api.get(`/files/${id}/stats`, {
                headers,
            });
            return response;
        } catch (error) {
            console.error('Erro ao fazer listagem do arquivo:', error);
                throw error;
        }
    },
    async updateDRM(id, enableDrm, token) {
        try {
            const headers = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            };
            const payload = {
                enableDrm: enableDrm
            };
            const response = await api.post(`/files/${id}/drm`, payload, {
                headers,
            });
            return response;
        } catch (error) {
            console.error('Erro ao atualizar DRM do arquivo:', error);
            throw error;
        }
    },
    async reprocessDrm(id, token) {
        try {
            const fileId = resolveFileApiId(id);
            if (!fileId) {
                throw new Error('ID do arquivo ausente');
            }
            const response = await api.request({
                method: 'POST',
                url: `/files/${fileId}/video/reprocess-drm`,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                transformRequest: [(data, headers) => {
                    delete headers['Content-Type'];
                    return data;
                }],
            });
            return response;
        } catch (error) {
            console.error('Erro ao reprocessar DRM do vídeo:', error);
            throw error;
        }
    }
}

function isVideoAsset(asset) {
    const type = String(asset?.type || '').toUpperCase();
    return type && type !== 'THUMBNAIL';
}

function assetPlaybackPaths(asset) {
    if (!asset) return [];
    const paths = [];
    const resolutions = asset.metadata?.resolutions;
    if (resolutions && typeof resolutions === 'object') {
        Object.values(resolutions).forEach((res) => {
            if (typeof res?.path === 'string' && res.path.trim()) {
                paths.push(res.path.trim());
            }
        });
    }
    if (typeof asset.path === 'string' && asset.path.trim()) {
        paths.push(asset.path.trim());
    }
    if (typeof asset.url === 'string' && asset.url.trim()) {
        paths.push(asset.url.trim());
    }
    return paths;
}

function isAbsoluteHttpUrl(url) {
    return typeof url === 'string' && /^https?:\/\//i.test(url.trim());
}

function resolveAssetPriority(asset) {
    const type = String(asset?.type || '').toUpperCase();
    if (type === 'ORIGINAL') return 0;
    if (type && type !== 'THUMBNAIL') return 1;
    return 2;
}

/**
 * URLs vindas explicitamente de fileAssets.path/url (ORIGINAL primeiro).
 */
export function resolveFileAssetPlaybackSrcCandidates(file) {
    const assets = collectFileAssets(file)
        .filter(isVideoAsset)
        .sort((a, b) => resolveAssetPriority(a) - resolveAssetPriority(b));

    const urls = assets
        .flatMap(assetPlaybackPaths)
        .filter((url) => isAbsoluteHttpUrl(url));

    return [...new Set(urls)];
}

function collectQualityPlaybackUrls(media) {
    if (!media || !Array.isArray(media.qualities)) return [];
    return media.qualities
        .map((q) => q?.path || q?.url || q?.key || q?.src)
        .filter((url) => typeof url === 'string' && url.trim())
        .map((url) => url.trim());
}

/**
 * URLs candidatas para reprodução direta do vídeo/PDF (CDN primeiro, S3 por último).
 * Com preferProcessed ou player embed, prioriza assets/qualities processados em vez do upload bruto.
 */
export function resolveMediaPlaybackSrcCandidates(file, options = {}) {
    const preferProcessed = options.preferProcessed === true;
    const media = file?.media;
    const assets = collectFileAssets(file);
    const qualityUrls = collectQualityPlaybackUrls(media);
    const assetUrls = resolveFileAssetPlaybackSrcCandidates(file);

    const original =
        assets.find((a) => String(a?.type || '').toUpperCase() === 'ORIGINAL')
        || assets.find(isVideoAsset);
    const originalPaths = assetPlaybackPaths(original);

    const processedPaths = assets
        .filter((asset) => {
            const type = String(asset?.type || '').toUpperCase();
            return isVideoAsset(asset) && type !== 'ORIGINAL';
        })
        .flatMap(assetPlaybackPaths);

    const uploadKey =
        typeof media?.key === 'string' && media.key.trim() ? media.key.trim() : '';
    const hasEmbedPlayer =
        typeof media?.playerUrl === 'string' && media.playerUrl.trim().length > 0;
    const useProcessedFirst = preferProcessed || hasEmbedPlayer;

    // Para PDFs, adicionar a URL diretamente como prioridade máxima
    const isPdf = media?.mediaType?.toLowerCase() === 'application/pdf';
    const pdfUrl = isPdf && uploadKey ? [uploadKey] : [];

    const ordered = useProcessedFirst
        ? [...pdfUrl, ...assetUrls, ...processedPaths, ...qualityUrls, uploadKey, ...originalPaths]
        : [...pdfUrl, ...assetUrls, uploadKey, ...processedPaths, ...qualityUrls, ...originalPaths];

    return [...new Set(ordered.filter((url) => typeof url === 'string' && url.trim()))];
}

/**
 * Melhor URL para player/captura de frame (prioriza CDN por CORS).
 */
export function resolveMediaPlaybackSrc(file, options = {}) {
    const candidates = resolveMediaPlaybackSrcCandidates(file, options);
    let src = candidates[0] || '';

    // Para PDFs, usar a URL do arquivo direto se disponível
    if (!src && file?.media?.mediaType?.toLowerCase() === 'application/pdf') {
        const media = file.media;
        src = media.key || media.url || media.path || '';
    }

    if (typeof src === 'string' && src.startsWith('blob:')) {
        return '';
    }
    return src;
}

function isNativeVideoPlaybackUrl(url) {
    if (typeof url !== 'string' || !url.trim()) return false;
    const path = url.toLowerCase().split('?')[0];
    if (/\.m3u8$/i.test(path) || /\.mpd$/i.test(path)) return false;
    if (/\.(mp3|wav|m4a|aac|ogg|flac)$/i.test(path)) return false;
    if (path.includes('/mp3/') || path.includes('/audio/')) return false;
    if (path.includes('mpegurl') || path.includes('/manifest')) return false;
    return true;
}

function nativeVideoPlaybackScore(url) {
    const path = url.toLowerCase().split('?')[0];
    if (/\.mp4$/i.test(path)) return 0;
    if (/\.webm$/i.test(path) || /\.mov$/i.test(path)) return 1;
    return 2;
}

/**
 * URLs para <video> no editor de corte (MP4 direto; evita HLS/DRM do player embed).
 */
export function resolveCutEditorPlaybackSrcCandidates(file) {
    const assetUrls = resolveFileAssetPlaybackSrcCandidates(file).filter(isNativeVideoPlaybackUrl);
    const standard = resolveMediaPlaybackSrcCandidates(file, { preferProcessed: false });
    const processed = resolveMediaPlaybackSrcCandidates(file, { preferProcessed: true });
    const merged = [...assetUrls, ...standard, ...processed];

    return [...new Set(merged.filter(isNativeVideoPlaybackUrl))].sort(
        (a, b) => nativeVideoPlaybackScore(a) - nativeVideoPlaybackScore(b)
    );
}

/** Melhor URL MP4 para o editor de corte. */
export function resolveCutEditorPlaybackSrc(file) {
    const candidates = resolveCutEditorPlaybackSrcCandidates(file);
    const src = candidates[0] || '';
    if (typeof src === 'string' && src.startsWith('blob:')) {
        return '';
    }
    return src;
}

/** Evita cache de CDN após reprocessamento no mesmo path. */
export function withPlaybackCacheVersion(url, version) {
    return withThumbnailVersion(url, { version });
}
